<template>
<p-modal name="exportInventoryByDccModal"
                 label="Export Inventory By DCC"
                 ref="exportInventoryByDccModal"
                 size="lg"
                 @show="loadParameters">
            <template v-slot:default>
                <div class="container-fluid content-wrapper">
                    <b-row cols="1" cols-md="2">
                        <b-col>
                            <p-select label="Fixture"
                                     v-model="reportParameters.fixture"
                                     :options="fixtureOptions"
                                     add-empty-option />
                            <p-select label="Area"
                                     v-model="reportParameters.area"
                                     :options="areaOptions"
                                     add-empty-option />
                            <p-select label="Summary or Detail"
                                      :options="includeDetailsOptions"
                                      v-model="reportParameters.includeDetails" />
                            <p-checkbox size="md"
                                        v-show="reportParameters.includeDetails"
                                        class="mt-3"
                                        label="Include fixture/area detail"
                                        v-model="
                                    reportParameters.includeFixtureAreaDetail
                                " />
                            <p-checkbox size="md"
                                        label="Include inventory not counted"
                                        v-model="
                                    reportParameters.includeInventoryNotCounted
                                " />
                            <p-checkbox size="md"
                                        label="Only include inventory where variance is not zero"
                                        v-model="
                                    reportParameters.includeOnlyNonZeroVariance
                                " />
                        </b-col>
                        <b-col>
                            <p-card class="mb-2 ps-2 py-2" no-body>
                                <label class="text-sm"
                                       v-if="dccSelectorHelpText">{{ dccSelectorHelpText }}</label>
                                <DCCsSelector ref="dccSelector" class="container-fluid"
                                              @selection-change="
                                        reportParameters.dccIds = $event
                                    " />
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate 
                        :user-report-template-options="userReportTemplateOptions"
                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                        :selected-user-report-template="selectedUserReportTemplate"
                        v-model:template-name="templateName"
                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </template>
            <template v-slot:modal-footer>
                <b-row>
                    <b-col>
                        <b-overlay :show="isBusy"
                                   rounded
                                   opacity="0.5"
                                   spinner-small
                                   spinner-variant="primary"
                                   class="d-inline-block">
                            <b-dropdown text="Export"
                                        variant="outline-primary">
                                <b-dropdown-item @click="exportInventoryByDccReport('pdf')">PDF</b-dropdown-item>
                                <b-dropdown-item @click="exportInventoryByDccReport('csv')">CSV</b-dropdown-item>
                            </b-dropdown>
                        </b-overlay>
                    </b-col>
                </b-row>
            </template>
        </p-modal>
</template>

<script>

import reports from './reports.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

export default {
    props: {},
    mixins: [reports, ReportTemplates],
    data() {
        return {
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Inventory By DCC',            
            includeDetailsOptions: [
                { text: 'Summary', value: false },
                { text: 'Detail', value: true }
            ],
        };
    },
     components: {
        UserReportTemplate
    },
    methods: {
        async exportInventoryByDccReport(type) {
            if (!(await this.saveUserReportTemplate())) {
                return;
            }

            let destination = type === 'pdf' ? '/physicalInventory/exportInventoryByDcc' : 'physicalInventory/exportInventoryByDccCsv';
            let message = `Inventory By DCC report generated successfully.`;
            let fileName = `Inventory By DCC ${this.reportParameters.includeDetails ? 'Detail' : 'Summary'}`;
            this.exportReport(type, destination, message, fileName);
        },

        async show() {
            this.$refs.dccSelector?.clearSelections();
            this.$refs.exportInventoryByDccModal.show();
            await this.loadUserReportTemplates();
        },

        // ReportTemplates hook: return false when anything in the saved template can no longer be
        // selected, and the mixin warns and loads nothing. Fixtures and areas come from the open
        // physical inventories for the current location and DCCs can be removed, so all three are
        // checked against their lists; the checkbox flags are literal values.
        async validateReportParameters(savedParameters) {
            // loadParameters kicks this off on @show without awaiting it, so the lists can still
            // be in flight here. Awaiting avoids rejecting a good template against empty lists.
            await this.loadFixtureAreaOptions();

            if (savedParameters.fixture
                && !this.fixtureOptions.some(fixtureOption => fixtureOption.value === savedParameters.fixture)) {
                return false;
            }

            if (savedParameters.area
                && !this.areaOptions.some(areaOption => areaOption.value === savedParameters.area)) {
                return false;
            }

            if (!this.includeDetailsOptions.some(includeDetailsOption => includeDetailsOption.value === savedParameters.includeDetails)) {
                return false;
            }

            return this.$refs.dccSelector.hasAllDccIds(savedParameters.dccIds);
        },

        // ReportTemplates hook: DCCsSelector owns its own tree selection, so the restored ids have
        // to be pushed back into it. Called unconditionally — a saved template with no DCCs has to
        // clear whatever is selected now, not be skipped.
        async onUserReportTemplateApplied() {
            await this.$refs.dccSelector.restoreSelections(this.reportParameters.dccIds);
        }
    }
};

</script>

<style scoped></style>
